/**
 * Get the file type from trace file name
 */
export type TraceFileType =
  | 'python'
  | 'javascript'
  | 'json'
  | 'markdown'
  | 'text'
  | 'log'
  | 'http'
  | 'unknown';

export function getTraceFileType(
  fileName: string,
  content?: string,
): TraceFileType {
  const lowerName = fileName.toLowerCase();

  if (lowerName.endsWith('.py')) return 'python';
  if (lowerName.endsWith('.js') || lowerName.endsWith('.ts'))
    return 'javascript';
  if (lowerName.endsWith('.json')) return 'json';

  if (content && probablyJson(content)) {
    return 'json';
  }

  if (lowerName.endsWith('.md')) return 'markdown';
  if (lowerName.endsWith('.txt')) return 'markdown';
  if (lowerName.endsWith('.log')) return 'log';
  if (
    lowerName.includes('http') ||
    lowerName.includes('request') ||
    lowerName.includes('response')
  )
    return 'http';

  // Check for code files by prefix patterns
  if (lowerName.startsWith('code_') && !lowerName.includes('result'))
    return 'python';
  if (lowerName.startsWith('code_result_')) return 'text';
  if (lowerName.startsWith('intelligence_')) return 'markdown';

  return 'unknown';
}

/**
 * Get the language for syntax highlighting
 */
export function getTraceFileLanguage(
  content: string,
  fileType: TraceFileType,
): string {
  switch (fileType) {
    case 'python':
      return 'python';
    case 'javascript':
      return 'javascript';
    case 'json':
      return 'json';
    case 'markdown':
      return 'markdown';
    case 'http':
      return 'http';
    default:
      return 'text';
  }
}

export function probablyJson(content: string): unknown | null {
  if (!content || typeof content !== 'string') {
    return null;
  }

  const trimmedContent = content.trim();
  if (!trimmedContent) {
    return null;
  }

  // For small files (< 10K chars), try full parse
  if (trimmedContent.length < 10000) {
    try {
      const parsed = JSON.parse(trimmedContent);
      // Only return if it's an object or array (not primitive types like strings/numbers)
      if (typeof parsed !== 'string') {
        return true;
      }
      return null;
    } catch {
      return null;
    }
  }

  // For large files (>= 10K chars), use pattern detection
  const first1k = trimmedContent.substring(0, 1000);
  const last1k = trimmedContent.substring(trimmedContent.length - 1000);

  // JSON patterns to look for
  const jsonStartPatterns = [
    /^\s*\{/, // Starts with {
    /^\s*\[/, // Starts with [
    /^\s*\{"[^"]+"\s*:\s*/, // Starts with {"key":
    /^\s*\[\s*\{/, // Starts with [{
  ];

  const jsonEndPatterns = [
    /\}\s*$/, // Ends with }
    /\]\s*$/, // Ends with ]
    /\}\s*\]\s*$/, // Ends with }]
  ];

  // Check if starts like JSON
  const startsLikeJson = jsonStartPatterns.some((pattern) =>
    pattern.test(first1k),
  );

  // Check if ends like JSON
  const endsLikeJson = jsonEndPatterns.some((pattern) => pattern.test(last1k));

  return startsLikeJson && endsLikeJson;
}
